import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaPlus, FaWhatsapp } from 'react-icons/fa6';
import { IoIosRefresh } from 'react-icons/io';
import { MdOutlineHandshake, MdViewList, MdGridView } from 'react-icons/md';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import ATable, { ATableHeader } from '../../organisms/ATable';
import BadgeStatus from '../../atoms/BadgeStatus';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import CurrencyInput from '../../molecules/CurrencyInput';
import PageFooter from '../../molecules/PageFooter';
import { deals, products, Deal, STAGES } from './mockData';
import appConfig from '../../../config/app-config';
import { Auth } from 'aws-amplify';

const JIRA_BASE_URL = 'https://agrolend.atlassian.net/browse';
const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;

const columns: ATableHeader[] = [
    { id: 'col-jira', title: 'Jira', field: 'jiraKey', canSort: true, width: '90px' },
    { id: 'col-product', title: 'Produto', field: 'productName', canSort: true, width: '220px' },
    { id: 'col-commercial', title: 'Comercial', field: 'commercial', canSort: true },
    { id: 'col-trader', title: 'Trader', field: 'trader', canSort: true },
    { id: 'col-assignor', title: 'Cedente', field: 'assignorName', canSort: true },
    { id: 'col-amount', title: 'Valor', field: 'amount', canSort: true, width: '140px' },
    { id: 'col-status', title: 'Estágio', field: 'stage', canSort: true, width: '180px' },
    { id: 'col-date', title: 'Data', field: 'createdAt', canSort: true, width: '100px' },
];

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink';

const stageColor = (stage: string): BadgeColor => {
    switch (stage) {
        case 'CONCLUIDO': return 'green';
        case 'REPROVADO_CANCELADO': return 'red';
        case 'NOVO_DEAL': return 'blue';
        default: return 'orange';
    }
};

const formatCurrency = (value: number, currency: 'BRL' | 'USD') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);

const productOptions = products.map(p => ({ value: p.id, label: p.name }));

const DealPageList = () => {
    const { view } = useParams<{ view?: string }>();
    const navigate = useNavigate();
    const viewMode = view === 'grid' ? 'grid' : 'table';
    const [showForm, setShowForm] = useState(false);
    const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
    const [showWhatsApp, setShowWhatsApp] = useState(false);
    const [whatsAppText, setWhatsAppText] = useState('');
    const [stageFilter, setStageFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Form state
    const [formProductId, setFormProductId] = useState('');
    const [formCommercial, setFormCommercial] = useState('');
    const [formTrader, setFormTrader] = useState('');
    const [formCnpj, setFormCnpj] = useState('');
    const [formCedenteName, setFormCedenteName] = useState('');
    const [formAmount, setFormAmount] = useState(0);

    // Cedente search
    const [searchCedente, setSearchCedente] = useState('');
    const [cedenteResults, setCedenteResults] = useState<any[]>([]);
    const [isSearchingCedente, setIsSearchingCedente] = useState(false);

    const handleCedenteSearch = async () => {
        if (searchCedente.length < 3) { setCedenteResults([]); return; }
        setIsSearchingCedente(true);
        try {
            const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
            const res = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${searchCedente}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY_PERSON || '' },
            });
            if (res.ok) {
                const data = await res.json();
                setCedenteResults((data.results || []).slice(0, 8));
            }
        } catch (e) { console.error(e); }
        finally { setIsSearchingCedente(false); }
    };

    const handleSelectCedente = (cedente: any) => {
        const doc = cedente.documents?.find((d: any) => d.type === 'CNPJ') || cedente.documents?.find((d: any) => d.type === 'CPF');
        setFormCnpj(doc?.number || '');
        setFormCedenteName(cedente.name);
        setCedenteResults([]);
        setSearchCedente('');
    };

    const selectedProduct = products.find(p => p.id === formProductId);
    const currency = selectedProduct?.currency || 'BRL';

    const filteredDeals = deals.filter((d: Deal) => {
        if (productFilter && d.productId !== productFilter) return false;
        if (stageFilter && d.stage !== stageFilter) return false;
        if (search && !d.assignorName.toLowerCase().includes(search.toLowerCase()) && !d.commercial.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const stageCounts = STAGES.reduce((acc, s) => {
        acc[s.value] = deals.filter(d => d.stage === s.value).length;
        return acc;
    }, {} as Record<string, number>);

    const productCounts = products.reduce((acc, p) => {
        acc[p.id] = deals.filter(d => d.productId === p.id).length;
        return acc;
    }, {} as Record<string, number>);

    const handleSubmit = () => {
        alert(`Deal criado!\nProduto: ${selectedProduct?.name}\nComercial: ${formCommercial}\nTrader: ${formTrader}\nCNPJ: ${formCnpj}\nValor: ${formatCurrency(formAmount, currency)}`);
        setShowForm(false);
        resetForm();
    };

    const resetForm = () => {
        setFormProductId('');
        setFormCommercial('');
        setFormTrader('');
        setFormCnpj('');
        setFormCedenteName('');
        setFormAmount(0);
        setSearchCedente('');
        setCedenteResults([]);
    };

    const parseWhatsApp = () => {
        if (!whatsAppText.trim()) return;
        const text = whatsAppText;
        const productMatch = text.match(/deal[:\s.]*([^\n]+)/i);
        const cedenteMatch = text.match(/cedente[:\s.]*([^\n]+)/i);
        const cnpjMatch = text.match(/(\d{2}[.\s]?\d{3}[.\s]?\d{3}[\/\s]?\d{4}[-\s]?\d{2})/);
        const valorMatch = text.match(/valor[^:]*[:\s.]*[R$USD\s]*([\d.,]+)\s*(mio|mi|mil|m\b)?/i);

        if (productMatch) {
            const name = productMatch[1].trim();
            const matched = products.find(p => p.name.toLowerCase().includes(name.toLowerCase().split('(')[0].trim()));
            if (matched) setFormProductId(matched.id);
        }
        if (cedenteMatch) setFormCommercial(cedenteMatch[1].trim());
        if (cnpjMatch) setFormCnpj(cnpjMatch[1]);
        if (valorMatch) {
            let val = parseFloat(valorMatch[1].replace(/\./g, '').replace(',', '.'));
            const suffix = (valorMatch[2] || '').toLowerCase();
            if (suffix === 'mio' || suffix === 'mi' || suffix === 'm') val *= 1000000;
            if (suffix === 'mil') val *= 1000;
            setFormAmount(val || 0);
        }

        setShowWhatsApp(false);
        setWhatsAppText('');
        setSelectedDeal(null);
        setShowForm(true);
    };

    const filterGroups = [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => { setStageFilter(''); setProductFilter(''); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!stageFilter && !productFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{deals.length}</span>
                        </div>
                    </button>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">ESTÁGIO</div>
                        <div className="space-y-1">
                            {STAGES.filter(s => stageCounts[s.value] > 0).map(s => (
                                <button key={s.value} onClick={() => setStageFilter(stageFilter === s.value ? '' : s.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${stageFilter === s.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span>{s.label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{stageCounts[s.value]}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">PRODUTO</div>
                        <div className="space-y-1">
                            {products.filter(p => productCounts[p.id] > 0).map(p => (
                                <button key={p.id} onClick={() => setProductFilter(productFilter === p.id ? '' : p.id)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${productFilter === p.id
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="truncate">{p.name}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{productCounts[p.id]}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            {!showForm && (
                <button onClick={() => { setSelectedDeal(null); setShowForm(true); }}
                    className={`fixed top-[69px] ${selectedDeal ? 'right-[580px]' : 'right-[10px]'} w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white`}
                    title="Novo Deal">
                    <FaPlus size={14} />
                </button>
            )}

            {!showForm && (
                <button onClick={() => setShowWhatsApp(true)}
                    className={`fixed top-[120px] ${selectedDeal ? 'right-[580px]' : 'right-[10px]'} w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-green-500 hover:bg-green-600 text-white`}
                    title="Importar do WhatsApp">
                    <FaWhatsapp size={18} />
                </button>
            )}

            {showWhatsApp && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center" onClick={() => setShowWhatsApp(false)}>
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-[500px] p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><FaWhatsapp className="text-green-500" /> Importar Deal do WhatsApp</h3>
                        <textarea
                            className="w-full h-48 border border-gray-300 dark:border-slate-600 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                            placeholder="Cole aqui o texto do WhatsApp com os dados do deal..."
                            value={whatsAppText}
                            onChange={e => setWhatsAppText(e.target.value)}
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => { setShowWhatsApp(false); setWhatsAppText(''); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Cancelar</button>
                            <button onClick={parseWhatsApp} disabled={!whatsAppText.trim()} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm disabled:opacity-50">
                                Preencher formulário
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <MdOutlineHandshake className="text-blue-600" size={18} />
                            <span>Deals</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            <div>v0.1.0</div>
                            <div className="font-medium text-green-600">SANDBOX</div>
                        </div>
                    </div>
                }
                leftSidebar={<FilterSidebar filterGroups={filterGroups} />}
                showRightPanel={showForm || !!selectedDeal}
                onCloseRightPanel={() => { setShowForm(false); setSelectedDeal(null); }}
                rightPanel={showForm ? (
                    <div className="h-full p-6 space-y-4">
                        <h2 className="text-lg font-semibold mb-4">Novo Deal</h2>
                        <SelectTextField label="Produto *" items={productOptions} value={formProductId} onChange={(e) => setFormProductId(e.target.value)} />
                        <TextEdit label="Comercial (Nome) *" value={formCommercial} onChange={(e) => setFormCommercial(e.target.value)} />
                        <TextEdit label="Trader (Nome) *" value={formTrader} onChange={(e) => setFormTrader(e.target.value)} />
                        {formCedenteName ? (
                            <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 p-3 rounded">
                                <div>
                                    <div className="text-xs text-gray-500 uppercase">Cedente</div>
                                    <div className="text-sm font-medium">{formCnpj} - {formCedenteName}</div>
                                </div>
                                <button onClick={() => { setFormCnpj(''); setFormCedenteName(''); }} className="text-red-500 hover:text-red-700 p-1" title="Remover cedente">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <TextEdit label="Buscar Cedente (CNPJ ou Nome) - Enter para buscar" value={searchCedente} onChange={(e) => setSearchCedente(e.target.value)} onKeyDown={(e: any) => e.key === 'Enter' && handleCedenteSearch()} />
                                {(isSearchingCedente || cedenteResults.length > 0) && (
                                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {isSearchingCedente ? (
                                            <div className="p-3 text-center text-sm text-gray-500"><div className="animate-spin inline-block rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" />Buscando...</div>
                                        ) : cedenteResults.map((c, i) => {
                                            const doc = c.documents?.find((d: any) => d.type === 'CNPJ') || c.documents?.find((d: any) => d.type === 'CPF');
                                            return (
                                                <div key={c.uuid || i} onClick={() => handleSelectCedente(c)} className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-600 cursor-pointer border-b border-gray-100 dark:border-slate-600 last:border-0">
                                                    <div className="text-sm font-medium">{c.name}</div>
                                                    <div className="text-xs text-gray-500">{doc?.number || '—'}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        <CurrencyInput label={`Valor (${currency === 'BRL' ? 'R$' : 'U$'}) *`} prefix={currency === 'BRL' ? 'R$' : 'U$'} value={formAmount} onChange={(value) => setFormAmount(value)} fractionDigits={2} />
                        <div className="pt-4 flex space-x-2">
                            <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Salvar</button>
                            <button onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm">Cancelar</button>
                        </div>
                    </div>
                ) : selectedDeal ? (
                    <div className="h-full p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">{selectedDeal.jiraKey}</h2>
                            <a href={`${JIRA_BASE_URL}/${selectedDeal.jiraKey}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Abrir no Jira</a>
                        </div>
                        <div className="space-y-3">
                            <div><label className="text-xs text-gray-500 uppercase">Produto</label><p className="font-medium">{selectedDeal.productName}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Estágio</label><p><BadgeStatus color={stageColor(selectedDeal.stage)} text={selectedDeal.stageFormatted} /></p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Comercial</label><p>{selectedDeal.commercial || '—'}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Trader</label><p>{selectedDeal.trader || '—'}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">CNPJ do Cedente</label><p>{selectedDeal.assignorCnpj || '—'}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Cedente</label><p>{selectedDeal.assignorName}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Valor</label><p className="font-medium">{selectedDeal.amount ? formatCurrency(selectedDeal.amount, selectedDeal.currency) : '—'}</p></div>
                        </div>
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Deals</h1>
                            <div className="flex items-center space-x-2">
                                <div className="flex items-center border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden">
                                    <button onClick={() => navigate('/deals/list')}
                                        className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                        title="Visualização em tabela">
                                        <MdViewList size={18} />
                                    </button>
                                    <button onClick={() => navigate('/deals/grid')}
                                        className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                        title="Visualização em grid">
                                        <MdGridView size={18} />
                                    </button>
                                </div>
                                <button className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar cedente ou comercial..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                        {viewMode === 'table' ? (
                            <ATable headers={columns}>
                                {filteredDeals.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500">Nenhum deal encontrado</td></tr>
                                ) : filteredDeals.map((deal, index) => (
                                    <tr key={deal.id}
                                        onClick={() => setSelectedDeal(deal)}
                                        className={`hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">
                                            <a href={`${JIRA_BASE_URL}/${deal.jiraKey}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{deal.jiraKey}</a>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium">{deal.productName}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">{deal.commercial}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">{deal.trader}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">{deal.assignorName}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-right">{formatCurrency(deal.amount, deal.currency)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center" onClick={e => e.stopPropagation()}>
                                            <a href={`${JIRA_BASE_URL}/${deal.jiraKey}`} target="_blank" rel="noopener noreferrer">
                                                <BadgeStatus color={stageColor(deal.stage)} text={deal.stageFormatted} />
                                            </a>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">{deal.createdAt}</td>
                                    </tr>
                                ))}
                            </ATable>
                        ) : (
                            <div className="grid grid-cols-3 gap-4 p-4">
                                {filteredDeals.length === 0 ? (
                                    <div className="col-span-3 py-8 text-center text-gray-500">Nenhum deal encontrado</div>
                                ) : filteredDeals.map(deal => (
                                    <div key={deal.id} onClick={() => setSelectedDeal(deal)} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white dark:bg-slate-800">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate" style={{maxWidth: '240px'}}>{deal.productName}</span>
                                            <a href={`${JIRA_BASE_URL}/${deal.jiraKey}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="truncate" style={{maxWidth: '140px'}}>
                                                <BadgeStatus color={stageColor(deal.stage)} text={deal.stageFormatted} />
                                            </a>
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                            <div className="font-medium text-gray-900 dark:text-white">{deal.assignorName}</div>
                                            <div><span className="text-gray-400">Comercial:</span> {deal.commercial}</div>
                                            <div><span className="text-gray-400">Trader:</span> {deal.trader}</div>
                                        </div>
                                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-slate-700">
                                            <a href={`${JIRA_BASE_URL}/${deal.jiraKey}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>{deal.jiraKey}</a>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(deal.amount, deal.currency)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={0} totalPages={1} itemsPerPage={filteredDeals.length} totalElements={filteredDeals.length} onPageChange={() => {}} />
                    </div>
                </div>
            </ThreeColumnLayout>
        </>
    );
};

export default DealPageList;
